import React from 'react'
import {
  Br,
  Cut,
  Line,
  Printer,
  render,
  Row,
  Text,
} from 'react-thermal-printer'
import { formatPrice } from '@/lib/utils'

export const usePrinter = () => {
  const [printer, setPrinter] = React.useState<any>(null)

  const requestPrinter = async () => {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [
          {
            vendorId: 0x0456, // Vendor ID for the printer
          },
        ],
      })
      setPrinter(device)
      return device
    } catch (e) {
      console.error('Error connecting to device:', e)
      return null
    }
  }

  const getPrinter = async () => {
    const devices = await navigator.usb.getDevices()
    if (devices.length > 0) {
      setPrinter(devices[0])
      return devices[0]
    }

    return null
  }

  const template = (receipt: any) => (
    <Printer type="star" width={48}>
      <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
        {receipt?.location?.receipt_header}
      </Text>
      <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
        {receipt?.location?.receipt_header_1}
      </Text>
      <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
        {receipt?.location?.receipt_header_2}
      </Text>
      <Text align="center">{receipt?.location?.address_1}</Text>
      <Text align="center">{receipt?.location?.address_2}</Text>
      <Text align="center">{receipt?.location?.city}</Text>

      <Line />

      <Row
        left="Invoice No"
        right={<Text>{receipt?.order?.invoice_number}</Text>}
      />
      <Row left="Order No" right={<Text>{receipt?.order?.id}</Text>} />
      <Row left="Date" right={<Text>{receipt?.order?.created_at}</Text>} />
      <Row
        left="Payment"
        right={<Text>{receipt?.order?.status_payment}</Text>}
      />

      <Line />

      <Row left="Items" right="" />

      <Line />

      {receipt?.order_items?.map((item: any, index: number) => {
        return (
          <>
            <Row
              key={index}
              left={
                <Text>
                  #{index + 1} - {item?.variant_name} / {item?.product_name} -{' '}
                  {formatPrice(item?.sub_total, 2, false)} x{' '}
                  {item?.sold_quantity}{' '}
                  {item?.discount_total > 0
                    ? `/ (${item?.discount_total} off)`
                    : ''}
                </Text>
              }
              right={<Text>{formatPrice(item?.total, 2, false)}</Text>}
              gap={5}
            />

            {item?.addon_options?.map((addon: any) => (
              <Row
                key={addon?.id}
                left={<Text>---- {addon?.name}</Text>}
                right={<Text>{formatPrice(addon?.price, 2, false)}</Text>}
                gap={5}
              />
            ))}
          </>
        )
      })}

      <Line />

      <Row left="Totals" right="" />

      <Line />

      {receipt?.order_totals?.map((item: any, index: number) => {
        if (item?.amount === 0) return null
        return (
          <Row
            key={index}
            left={<Text>{item?.total_name}</Text>}
            right={<Text>{formatPrice(item?.amount, 2, false)}</Text>}
            gap={2}
          />
        )
      })}

      <Line />

      <Row left="Payments" right="" />

      <Line />

      {receipt?.payment_summary?.payments?.map((item: any, index: number) => {
        if (item?.amount === 0) return null
        return (
          <Row
            key={index}
            left={
              <Text>
                {item?.code?.charAt(0).toUpperCase() + item?.code?.slice(1)}
              </Text>
            }
            right={<Text>{formatPrice(item?.amount_paid, 2, false)}</Text>}
            gap={2}
          />
        )
      })}

      <Line />

      <Row
        left={<Text>Customer Paid</Text>}
        right={
          <Text>
            {formatPrice(receipt?.payment_summary?.total_paid, 2, false)}
          </Text>
        }
        gap={2}
      />
      <Row
        left={<Text>Balance to Customer</Text>}
        right={
          <Text>
            {formatPrice(receipt?.payment_summary?.over_paid, 2, false)}
          </Text>
        }
        gap={2}
      />

      <Line />

      <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
        {receipt?.location?.receipt_footer}
      </Text>

      <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
        {receipt?.location?.receipt_footer_1}
      </Text>

      <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
        {receipt?.location?.receipt_footer_2}
      </Text>

      <Br />

      <Text align="center">System by et.lk</Text>

      <Cut />
    </Printer>
  )

  const printReceipt = async (receipt: any) => {
    let printer = await getPrinter()

    if (!printer) {
      printer = await requestPrinter()
    }

    const printData: Uint8Array = await render(template(receipt))

    try {
      await printer.open()
      await printer.selectConfiguration(1)
      await printer.claimInterface(0)

      await printer.transferOut(
        printer.configuration.interfaces[0].alternate.endpoints.find(
          (ep: any) => ep.direction === 'out',
        ).endpointNumber,
        printData,
      )

      await printer.close()
    } catch (e) {
      console.error('Error printing receipt:', e)
    }
  }

  return {
    printer,
    requestPrinter,
    getPrinter,
    template,
    printReceipt,
  }
}
